import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { fetchMe, login as apiLogin, logout as apiLogout } from "../api/auth";
import type { AuthUser } from "../api/auth";

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthModalOpen: boolean;
  showWelcomeToast: boolean;
  showSignOutToast: boolean;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  dismissWelcomeToast: () => void;
  dismissSignOutToast: () => void;
  signIn: (email: string, password: string, code?: string) => Promise<{
    requiresCode: boolean;
  }>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface Props {
  children: ReactNode;
}

export function AuthProvider({ children }: Props) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);
  const [showSignOutToast, setShowSignOutToast] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    const me = await fetchMe();
    setUser(me);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const me = await fetchMe();
      if (!cancelled) {
        setUser(me);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openAuthModal = useCallback(() => setIsAuthModalOpen(true), []);
  const closeAuthModal = useCallback(() => setIsAuthModalOpen(false), []);
  const dismissWelcomeToast = useCallback(() => setShowWelcomeToast(false), []);
  const dismissSignOutToast = useCallback(() => setShowSignOutToast(false), []);

  const signIn = useCallback(
    async (
      email: string,
      password: string,
      code?: string,
    ): Promise<{ requiresCode: boolean }> => {
      const result = await apiLogin({ email, password, code });
      if (result.requiresCode) {
        return { requiresCode: true };
      }
      if (!result.user) {
        throw new Error("Login failed");
      }
      setUser(result.user);
      setIsAuthModalOpen(false);
      setShowWelcomeToast(true);
      return { requiresCode: false };
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await apiLogout();
    setUser(null);
    queryClient.clear();
    setShowSignOutToast(true);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthModalOpen,
      showWelcomeToast,
      showSignOutToast,
      openAuthModal,
      closeAuthModal,
      dismissWelcomeToast,
      dismissSignOutToast,
      signIn,
      signOut,
      refresh,
    }),
    [
      user,
      isLoading,
      isAuthModalOpen,
      showWelcomeToast,
      showSignOutToast,
      openAuthModal,
      closeAuthModal,
      dismissWelcomeToast,
      dismissSignOutToast,
      signIn,
      signOut,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
